import navigationConstants from '@navigation/navigationConstants';
import { NavigationProps } from '@navigation/types';
import { FEATURE_FLAGS, SECURITY_PIN_INFO_MODAL } from '@root/constants';
import { FeatureFlagRecord, ModalState } from '@root/types';
import { PinRegister } from '@root/types/securityPin';
import { UseQueryResult } from '@tanstack/react-query';
import { getSecurityPinErrorCode } from '@molecules/ProfileSecurityPin';
import { returnBottomSheetContent } from '@utils/utils';
import type React from 'react';

interface ProfilePhonChangeUtilsParams {
  setInfoModal: React.Dispatch<React.SetStateAction<ModalState>>;
  infoModal: ModalState;
  navigation: NavigationProps;
  isFeatureAvailable: FeatureFlagRecord[];
  pinRegister: PinRegister;
  queryPinOpen: UseQueryResult;
  onPinOpenSuccess?: () => void;
}

export default function profilePhoneChangeActiveBottomSheetHandler(params: ProfilePhonChangeUtilsParams) {
  const { setInfoModal, infoModal, navigation, isFeatureAvailable, pinRegister, queryPinOpen, onPinOpenSuccess } =
    params;

  const checkPin = () => {
    if (
      !isFeatureAvailable[FEATURE_FLAGS.SECURITY_PIN]?.show ||
      !isFeatureAvailable[FEATURE_FLAGS.PHONE_NUMBER_CHANGE]?.show
    ) {
      return;
    }

    // If not Enrolled to Security PIN
    // Show Bottom Sheet
    if (!pinRegister.isActive) {
      return setInfoModal({
        data: returnBottomSheetContent(
          SECURITY_PIN_INFO_MODAL.NEED_CREATE_PIN,
          SECURITY_PIN_INFO_MODAL.NEED_CREATE_PIN,
        ),
        errorCode: 'NEED_CREATE_PIN`',
        visible: true,
      });
    }

    // If Enrolled, Fetch Security Open and navigate only when this refetch succeeds (not when Profile refetch succeeds)
    if (pinRegister.isActive) {
      return queryPinOpen.refetch().then((result: any) => {
        if (result?.data && onPinOpenSuccess) {
          onPinOpenSuccess();
          return;
        }
        // Show locked modal only when the user explicitly tapped the row and the
        // API returns GG-SEC-PIN-09 — NOT via the shared query cache being updated
        // by ProfileSecurityPin's mount-refetch.
        if (result?.error) {
          const errorCode = getSecurityPinErrorCode(result.error);
          if (errorCode === 'GG-SEC-PIN-09') {
            checkPinError();
          }
        }
      });
    }
  };

  const checkPinError = () => {
    setInfoModal({
      visible: true,
      data: returnBottomSheetContent(
        SECURITY_PIN_INFO_MODAL.VERIFY_PIN_LOCKED,
        SECURITY_PIN_INFO_MODAL.VERIFY_PIN_LOCKED,
      ),
      errorCode: 'VERIFY_PIN_LOCKED',
    });
  };

  const onPrimaryPress = () => {
    setInfoModal({
      ...infoModal,
      visible: false,
    });

    if (infoModal.errorCode === 'VERIFY_PIN_LOCKED') {
      return true;
    }
    if (infoModal.errorCode !== 'GG_01_FALLBACK') {
      navigation.navigate(navigationConstants.GG_CREATE_PIN, {
        type: 'create',
        source: 'phone_change',
      });
    }
  };

  const onCloseModal = () => {
    setInfoModal({
      ...infoModal,
      visible: false,
    });
  };

  return { checkPin, checkPinError, onPrimaryPress, onCloseModal };
}
